import { oc } from "@orpc/contract";
import { z } from "zod";

const UUIDSchema = z.uuid();

export const CompanyListItemSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  originId: z.string().nullable(),
  shortName: z.string().nullable(),
  email: z.string().nullable(),
  lastUploadedAt: z.date().nullable(),
  /** True when the company has a ship-and-debit entity module config row. */
  shipAndDebitEnabled: z.boolean(),
});

export const ListCompaniesInputSchema = z.object({
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(20),
  search: z.string().optional(),
});

export const ListCompaniesResponseSchema = z.object({
  companies: z.array(CompanyListItemSchema),
  totalCount: z.number().int().nonnegative(),
});

export const CsvDocumentSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  createdAt: z.date(),
});

export const CompanyPricelistDocumentSchema = z.object({
  id: UUIDSchema,
  associationId: UUIDSchema,
  name: z.string(),
  mimeType: z.string().nullable(),
  fileSizeBytes: z.number().nullable(),
  createdAt: z.date(),
  uploadedByName: z.string().nullable(),
  csvDocuments: z.array(CsvDocumentSchema),
});

export const ListCompanyPricelistsInputSchema = z.object({
  companyId: z.uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const ListCompanyPricelistsResponseSchema = z.object({
  documents: z.array(CompanyPricelistDocumentSchema),
  totalCount: z.number().int().nonnegative(),
});

export const CompanyBasicInfoSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

export const CompaniesBySupplierIdsInputSchema = z.object({
  supplierIds: z.array(UUIDSchema).min(1).max(500),
});

// Response is a record mapping supplierId -> company basic info (or null if no company found)
export const CompaniesBySupplierIdsResponseSchema = z.record(
  z.string(),
  CompanyBasicInfoSchema.nullable(),
);

export const CompanyIdInputSchema = z.object({
  companyId: z.uuid(),
});

export const AddSupplierResponseSchema = z.object({
  id: z.uuid(),
});

export const AddCustomerResponseSchema = z.object({
  id: z.uuid(),
});

const CompanyActionSuccessSchema = z.object({
  success: z.literal(true),
});

export const CompanyDistinctValuesInputSchema = z.object({
  column: z.string(),
  search: z.string().optional(),
});

export const CompanyDistinctValuesResponseSchema = z.object({
  values: z.array(z.string()),
});

export const companiesContracts = {
  bySupplierIds: oc
    .input(CompaniesBySupplierIdsInputSchema)
    .output(CompaniesBySupplierIdsResponseSchema)
    .route({ method: "GET" }),
  addSupplier: oc
    .input(CompanyIdInputSchema)
    .output(AddSupplierResponseSchema)
    .route({ method: "POST" }),
  removeSupplier: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({ method: "POST" }),
  addCustomer: oc
    .input(CompanyIdInputSchema)
    .output(AddCustomerResponseSchema)
    .route({ method: "POST" }),
  removeCustomer: oc
    .input(CompanyIdInputSchema)
    .output(CompanyActionSuccessSchema)
    .route({ method: "POST" }),
  distinctValues: oc
    .input(CompanyDistinctValuesInputSchema)
    .output(CompanyDistinctValuesResponseSchema)
    .route({
      method: "GET",
      summary: "Get distinct values for a company column",
    }),
  listCompanies: oc
    .input(ListCompaniesInputSchema)
    .output(ListCompaniesResponseSchema)
    .route({ method: "GET" }),
  listCompanyPricelists: oc
    .input(ListCompanyPricelistsInputSchema)
    .output(ListCompanyPricelistsResponseSchema)
    .route({ method: "GET" }),
} as const;

export type CompanyBasicInfo = z.infer<typeof CompanyBasicInfoSchema>;
export type CompaniesBySupplierIdsInput = z.infer<
  typeof CompaniesBySupplierIdsInputSchema
>;
export type CompaniesBySupplierIdsResponse = z.infer<
  typeof CompaniesBySupplierIdsResponseSchema
>;
export type CompanyDistinctValuesInput = z.infer<
  typeof CompanyDistinctValuesInputSchema
>;
export type CompanyListItem = z.infer<typeof CompanyListItemSchema>;
export type ListCompaniesInput = z.infer<typeof ListCompaniesInputSchema>;
export type ListCompaniesResponse = z.infer<typeof ListCompaniesResponseSchema>;
export type CsvDocument = z.infer<typeof CsvDocumentSchema>;
export type CompanyPricelistDocument = z.infer<
  typeof CompanyPricelistDocumentSchema
>;
export type ListCompanyPricelistsInput = z.infer<
  typeof ListCompanyPricelistsInputSchema
>;
export type ListCompanyPricelistsResponse = z.infer<
  typeof ListCompanyPricelistsResponseSchema
>;
